import React, { createRef, useEffect, useState } from 'react';

import SkeletonLoader from 'components/SkeletonLoader';
import styled, { css } from 'styled-components';
import { HideScrollbarCSS, StyledButton, StyledDivider, StyledTypography } from 'styles/common';

import theme from 'constants/theme';

const ClickToScrollNav = ({
  sectionList = [], // Array of type { id, header, component}
  loading = false,
  showDividers = false,
  className,
  defaultSelectedIndex = 0, // index of default selected when renders,
  scrollToId,
  forceUpdateValue,
  isOneOnOne = false,
}) => {
  const [selectedNav, setSelectedNav] = useState(
    sectionList.length > 0 ? sectionList[defaultSelectedIndex].scrollId : ''
  );
  const [navCompRefs, setNavCompRefs] = useState({});

  const scrollToComponentTop = (ref) => {
    try {
      ref.current.scrollIntoView({
        behavior: 'smooth',
      });
    } catch (err) {
      console.error('Error scrolling ref into view :', err);
    }
  };

  useEffect(() => {
    // check if section has same ids as before then we don't need to update NavCompRefs.

    // noticed that, if we delete a section that won't be reflected immediately.
    // currently not applicable in our produce.
    // TODO: fix and test this scenario.

    // const sectionsWithRef = Object.keys(navCompRefs);
    const sectionsFromList = sectionList.map(({ scrollId }) => scrollId);

    if (sectionsFromList.some((sectionId) => !navCompRefs[sectionId])) {
      setNavCompRefs((refs) => {
        const newRefObj = {};
        sectionList.forEach(({ scrollId }) => {
          newRefObj[scrollId] = refs[scrollId] || createRef();
        });
        return newRefObj;
      });
    }
  }, [sectionList, navCompRefs]);

  useEffect(() => {
    if (selectedNav && Object.keys(navCompRefs).length > 0) {
      scrollToComponentTop(navCompRefs[selectedNav]);
    }
    if (scrollToId && Object.keys(navCompRefs).length > 0) {
      // checking if scroll to id is valid ref id
      if (Object.keys(navCompRefs).includes(scrollToId)) {
        setSelectedNav(scrollToId);
        scrollToComponentTop(navCompRefs[scrollToId]);
      }
    }
  }, [navCompRefs, scrollToId, forceUpdateValue]);

  return (
    <>
      {loading ? (
        <SkeletonLoader skeletons={['95%', '95%', '95%', '95%']} />
      ) : (
        <StyledContainer className={className}>
          <NavigationBar>
            {sectionList.map(({ scrollId, header, rightGutter }, index) => (
              <NavItem
                key={scrollId}
                isSelected={scrollId === selectedNav}
                handleOnClick={() => {
                  setSelectedNav(scrollId);
                  scrollToComponentTop(navCompRefs[scrollId]);
                }}
                rightGutter={rightGutter}
              >
                <StyledTypography
                  color={scrollId === selectedNav ? theme.palette.primary.light : 'textSecondary'}
                  cursor="pointer"
                >
                  {header({ isOneOnOne })}
                </StyledTypography>
              </NavItem>
            ))}
          </NavigationBar>
          <ScrollableContainer>
            {sectionList.map(({ scrollId, component }, index) => (
              <div ref={navCompRefs[scrollId]} key={scrollId}>
                {component()}
                {showDividers && index !== sectionList.length - 1 && <StyledDivider variant="fullWidth" />}
              </div>
            ))}
          </ScrollableContainer>
        </StyledContainer>
      )}
    </>
  );
};

const NavItem = ({ children, isSelected = false, handleOnClick, rightGutter = 2 }) => (
  <StyledNavButton
    disableFocusRipple
    disableRipple
    onClick={handleOnClick}
    selected={isSelected}
    rightGutter={rightGutter}
  >
    {children}
  </StyledNavButton>
);

const ScrollableContainer = styled.div`
  position: relative;
  height: calc(100% - 4rem);
  overflow-y: auto;
  top: 4rem;
  ${HideScrollbarCSS};
  padding-right: 1rem;
`;

const NavigationBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 1rem;
  border-bottom: 1px solid lightgrey;
`;

const StyledContainer = styled.div`
  position: relative;
  flex: 1;
  overflow: hidden;
  margin: 0;
  ${({ disabled }) =>
    disabled &&
    `
    pointer-events: none;
    opacity: 0.3;
  `};
`;

const blueBorderCSS = css`
  border-bottom: 3px solid ${(props) => props.theme.palette.primary.main};
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
`;

const StyledNavButton = styled(StyledButton)`
  border-bottom: 3px solid transparent;
  ${(props) => props.selected && blueBorderCSS};
`;

export default ClickToScrollNav;
